import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { CommentDataEntry, DataEntry } from '@osos/shared/api';
import {
  CommentPresenterModel,
  DataEntryPresenterModel,
  GROUP_ENTITY_ID,
  IconModel,
  LoadingIndicator,
  LoadingType,
  MentionNode,
  RefProperty,
  USER_ENTITY_ID,
} from '@osos/shared/types';
import { Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { CommentFactoryService } from '../../factories';
import { ApiUtilsService, DataCommentsService } from '../../services';
import { isFileModel } from '../../utils';
import { LoadingActions } from '../loading/loading.actions';
import { UserState } from '../user/user.state';
import { CommentActions } from './comment.actions';

export interface CommentStateModel {
  presentedComments: Record<string, Array<CommentPresenterModel>>;
  loading: LoadingIndicator;
  transitoryMentionList: MentionNode[];
  lastCreatedCommentMeta: {
    mentions: MentionNode[];
  };
}

@State<CommentStateModel>({
  name: 'comments',
  defaults: {
    presentedComments: {},
    loading: new LoadingIndicator(),
    transitoryMentionList: [],
    lastCreatedCommentMeta: {
      mentions: [],
    },
  },
})
@Injectable()
export class CommentState {
  constructor(
    private commentFactory: CommentFactoryService,
    private commentService: DataCommentsService,
    private store: Store,
    private apiHelper: ApiUtilsService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------

  static presentedComments(entryId: string) {
    return createSelector(
      [CommentState],
      (state: CommentStateModel) => state.presentedComments[entryId],
    );
  }

  static loading(type: LoadingType) {
    return createSelector(
      [CommentState],
      (state: CommentStateModel) => state.loading[type],
    );
  }

  @Selector([CommentState])
  static transitoryMentionList(state: CommentStateModel): MentionNode[] {
    return state.transitoryMentionList;
  }
  // --------------------------------- ACTIONS --------------------------------

  @Action(CommentActions.CreateComment)
  createComment(
    { setState, getState, dispatch }: StateContext<CommentStateModel>,
    { entityId, entryId, comment }: CommentActions.CreateComment,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));
    setState(
      patch<CommentStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    const lastCreatedCommentMeta = getState().lastCreatedCommentMeta;
    return this.commentService
      .createComment({
        entity: entityId,
        relatedToId: entryId,
        body: {
          ...comment,
          audience: comment.audience
            ? comment.audience.map((member) => ({
                dataEntity: member.dataEntity,
                dataEntry: member.dataEntry,
              }))
            : undefined,
        },
      })
      .pipe(
        tap((comment) => {
          const newComment = this.commentFactory.createPresenterModel(comment);
          const comments = getState().presentedComments[entryId];
          setState(
            patch<CommentStateModel>({
              presentedComments: patch({
                [entryId]: [...comments, newComment],
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<CommentStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }
  // Not implemented yet
  @Action(CommentActions.UpdateComment)
  updateComment(
    { setState, dispatch }: StateContext<CommentStateModel>,
    {
      entityId,
      entryId,
      commentId,
      comment: { audience },
    }: CommentActions.UpdateComment,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));
    setState(
      patch<CommentStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    const audienceToSend = audience.map((member) => ({
      dataEntity: member.dataEntity,
      dataEntry: member.dataEntry,
    }));
    return this.commentService
      .updateComment({
        entity: entityId,
        relatedToId: entryId,
        commentId,
        body: { audience: audienceToSend },
      })
      .pipe(
        tap(() => {
          setState(
            patch<CommentStateModel>({
              presentedComments: patch({
                [entryId]: updateItem(
                  (item) => item.id === commentId,
                  patch<CommentPresenterModel>({
                    audience: audience?.length
                      ? {
                          users: audience.filter(
                            (item) => item.dataEntity === USER_ENTITY_ID,
                          ),
                          groups: audience.filter(
                            (item) => item.dataEntity === GROUP_ENTITY_ID,
                          ),
                        }
                      : null,
                  }),
                ),
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<CommentStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(CommentActions.UpdateLastCreatedCommentMeta)
  updateLastCreatedCommentMeta(
    { setState }: StateContext<CommentStateModel>,
    { payload }: CommentActions.UpdateLastCreatedCommentMeta,
  ) {
    setState(
      patch<CommentStateModel>({
        lastCreatedCommentMeta: {
          mentions: payload.mentions,
        },
      }),
    );
  }

  @Action(CommentActions.TransitoryFetchMentionList, {
    cancelUncompleted: true,
  })
  transitoryFetchMentionList(
    { setState }: StateContext<CommentStateModel>,
    { payload }: CommentActions.TransitoryFetchMentionList,
  ): Observable<any> {
    return this.commentService
      .getMentionsList({
        keyword: payload.searchTerm,
        entityId: payload.entityId,
        entryId: payload.entryId,
      })
      .pipe(
        map((refs) =>
          // localized will be supported
          refs.map((ref) => ({
            label: ref?._resolved?.mainTitle,
            value: ref.dataEntry,
            nodeType: ref.dataEntity,
            ...(!!ref?._resolved?.mainPicture &&
              isFileModel(ref?._resolved?.mainPicture) && {
                image: ref?._resolved?.mainPicture,
              }),
            ...(!!ref?._resolved?.mainPicture &&
              !isFileModel(ref?._resolved?.mainPicture) && {
                icon: ref?._resolved?.mainPicture,
              }),
          })),
        ),
        tap((mentionList) => {
          setState(
            patch<CommentStateModel>({
              transitoryMentionList: mentionList,
            }),
          );
        }),
      );
  }

  @Action(CommentActions.FetchComments)
  fetchComments(
    { setState }: StateContext<CommentStateModel>,
    { entityId, entryId }: CommentActions.FetchComments,
  ): Observable<any> {
    setState(
      patch<CommentStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );
    const filter = this.apiHelper.prepareFilter<DataEntry>({
      order: ['creationInfo.doneAt ASC'],
    });
    return this.commentService
      .getCommentsRelatedTo({
        entity: entityId,
        relatedToId: entryId,
        filter,
      })
      .pipe(
        tap((comments: CommentDataEntry[]) => {
          const presenterComments: CommentPresenterModel[] = comments.map(
            (comment) => this.commentFactory.createPresenterModel(comment),
          );
          setState(
            patch<CommentStateModel>({
              presentedComments: patch({
                [entryId]: presenterComments,
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<CommentStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  private getCurrentUser(): DataEntryPresenterModel {
    return this.store.selectSnapshot(UserState.currentUser);
  }
}
